import { useEffect, useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { BG_COLORS, GRID_COLORS, INK_COLORS, SKETCH_VIEWPORT_COLORS } from '../themes/colors';
import {
    VIEWPORT_COLOR_TOKENS,
    useViewportColorsStore,
    type ViewportColorMode,
} from '../state/useViewportColorsStore';

/**
 * Resolved suite color mode ('light' | 'dark') observed from the `data-theme`
 * attribute the suite runtime owns on <html>, with the same system fallback
 * the token sheet uses: an unset attribute means "system", resolved through
 * the OS preference. Pure DOM observation — safe to call outside the R3F
 * canvas (shells, overlays) where useThree is unavailable.
 */
export function useSuiteColorMode(): 'light' | 'dark' {
    const resolve = (): 'light' | 'dark' =>
        document.documentElement.getAttribute('data-theme') === 'light'
            ? 'light'
            : document.documentElement.getAttribute('data-theme') === 'dark'
                ? 'dark'
                : (window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark');
    const [mode, setMode] = useState<'light' | 'dark'>(resolve);

    useEffect(() => {
        const sync = () => setMode(resolve());
        sync();
        // The runtime flips the attribute; the media listener covers the OS
        // flip while in "system" mode (no attribute write happens there).
        const observer = new MutationObserver(sync);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        const query = window.matchMedia?.('(prefers-color-scheme: light)');
        query?.addEventListener?.('change', sync);
        return () => {
            observer.disconnect();
            query?.removeEventListener?.('change', sync);
        };
    }, []);

    return mode;
}

/**
 * True while the app runs in dark color mode. The suite runtime owns the
 * `data-theme` attribute on <html>; this hook observes it directly, which
 * also works inside the R3F canvas (no React context bridged there). Must be
 * used inside the Canvas so the theme flip can invalidate the demand render
 * loop.
 */
export function useIsDarkMode(): boolean {
    const isDark = useSuiteColorMode() === 'dark';
    const invalidate = useThree((state) => state.invalidate);

    // frameloop="demand": a color-mode switch mutates no zustand store, so
    // re-render alone never repaints the canvas. Force a frame on change.
    useEffect(() => { invalidate(); }, [isDark, invalidate]);

    return isDark;
}

/** Resolved viewport palette of one workbench mode's token sheet. */
export interface ViewportPalette {
    background: string;
    gridMajor: string;
    gridMinor: string;
    gridSection: string;
}

/** Theme fallbacks (the dark reference constants in themes/colors.ts). */
const SHARED_FALLBACK: ViewportPalette = {
    background: BG_COLORS.viewport,
    gridMajor: GRID_COLORS.major,
    gridMinor: GRID_COLORS.minor,
    gridSection: GRID_COLORS.section,
};

const VIEWPORT_COLOR_FALLBACKS: Record<ViewportColorMode, ViewportPalette> = {
    '2d': SHARED_FALLBACK,
    '3d': SHARED_FALLBACK,
    bim: SHARED_FALLBACK,
    sketch: {
        background: SKETCH_VIEWPORT_COLORS.background,
        gridMajor: SKETCH_VIEWPORT_COLORS.gridMajor,
        gridMinor: SKETCH_VIEWPORT_COLORS.gridMinor,
        gridSection: SKETCH_VIEWPORT_COLORS.gridSection,
    },
};

/**
 * Live viewport palette for a workbench mode, read from the mode-scoped
 * token sheet (global.css): each mode resolves through its own alias, so
 * drawn geometry matches the CSS canvas behind it — the token sheet follows
 * the `data-theme` attribute, and any Settings › Color override pinned
 * inline on :root wins over the alias. The themes/colors.ts constants are
 * the fallbacks. DOM-only — safe in workbench shells; canvas-internal
 * consumers re-render on edits and the repaint mounts cover the demand loop.
 */
export function useViewportThemeColors(mode: ViewportColorMode = '3d'): ViewportPalette {
    const isDark = useSuiteColorMode() === 'dark';
    // Editor edits rewrite the inline :root tokens between renders; the
    // revision re-reads them (and re-renders canvas consumers).
    const revision = useViewportColorsStore((s) => s.revision);
    return useMemo(() => {
        const styles = getComputedStyle(document.documentElement);
        const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;
        const tokens = VIEWPORT_COLOR_TOKENS[mode];
        const fallback = VIEWPORT_COLOR_FALLBACKS[mode];
        return {
            background: read(tokens.background, fallback.background),
            gridMajor: read(tokens.gridMajor, fallback.gridMajor),
            gridMinor: read(tokens.gridMinor, fallback.gridMinor),
            gridSection: read(tokens.gridSection, fallback.gridSection),
        };
    }, [isDark, mode, revision]);
}

/**
 * Ink color for model/detail/annotation geometry: the DEFAULT LINE COLOR
 * every view model (sketch, 2D, 3D, BIM) resolves to when an entity carries
 * no explicit color. Reads the `--color-ink-viewport` token from the
 * app-local token sheet (black on the light viewport, white on the dark
 * one), falling back to the INK_COLORS dark reference.
 */
export function useAnnotationInk(): string {
    const isDark = useIsDarkMode();
    return useMemo(() => {
        const token = getComputedStyle(document.documentElement)
            .getPropertyValue('--color-ink-viewport').trim();
        return token || (isDark ? INK_COLORS.viewport : '#000000');
    }, [isDark]);
}
